import { Suspense, lazy, useMemo, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Toolbar,
  Typography,
  Button,
  Divider,
  List,
  ListItem,
  ListItemButton,
  Chip,
  Alert,
  IconButton,
  LinearProgress,
} from '@mui/material';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RouteIcon from '@mui/icons-material/Route';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import PanoramaFishEyeIcon from '@mui/icons-material/PanoramaFishEye';
import WarningIcon from '@mui/icons-material/Warning';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { useGisStore } from '@/stores/useGisStore.js';
import { useAuthStore } from '@/stores/useAuthStore.js';
import { useGisRealtime } from '@/hooks/useGisRealtime.js';
import { useDemoFlight } from '@/hooks/useDemoFlight.js';
import { Permission } from '@k-ucap/shared';
import { Disclaimer } from '@/components/common/Disclaimer.js';
import { GisErrorBoundary } from './GisErrorBoundary.js';
import { mockRoute } from '@/mock/data.js';
import type { VehicleState } from '@k-ucap/shared';
import type { GisAlert } from '@/domain/gis/GisMessageProjectionService.js';

const CesiumMap = lazy(() => import('./CesiumMap.js'));

const statusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
  const s = status.toUpperCase();
  if (s.includes('CRITICAL') || s.includes('FAIL') || s.includes('DANGER')) return 'error';
  if (s.includes('WARN')) return 'warning';
  if (s.includes('CRUISE') || s.includes('NORMAL') || s.includes('INERTIAL')) return 'success';
  return 'default';
};

export const GisPage = () => {
  useGisRealtime();
  const [searchParams] = useSearchParams();
  const preselectedVehicleId = searchParams.get('vehicleId');

  const vehicles = useGisStore((s) => s.vehicles);
  const tracks = useGisStore((s) => s.tracks);
  const routes = useGisStore((s) => s.routes);
  const alerts = useGisStore((s) => s.alerts);
  const selectedVehicleId = useGisStore((s) => s.selectedVehicleId);
  const followVehicle = useGisStore((s) => s.followVehicle);
  const showTracks = useGisStore((s) => s.showTracks);
  const showRoutes = useGisStore((s) => s.showRoutes);
  const showAlertPanel = useGisStore((s) => s.showAlertPanel);
  const demoRunning = useGisStore((s) => s.demoRunning);

  const selectVehicle = useGisStore((s) => s.selectVehicle);
  const toggleFollowVehicle = useGisStore((s) => s.toggleFollowVehicle);
  const toggleShowTracks = useGisStore((s) => s.toggleShowTracks);
  const toggleShowRoutes = useGisStore((s) => s.toggleShowRoutes);
  const toggleShowAlertPanel = useGisStore((s) => s.toggleShowAlertPanel);
  const setAutoFit = useGisStore((s) => s.setAutoFit);
  const clearTrack = useGisStore((s) => s.clearTrack);
  const acknowledgeAlert = useGisStore((s) => s.acknowledgeAlert);
  const clearAlerts = useGisStore((s) => s.clearAlerts);
  const stopDemo = useGisStore((s) => s.stopDemo);
  const setRoute = useGisStore((s) => s.setRoute);

  const hasDemoPermission = useAuthStore((s) => s.hasPermission(Permission.SCENARIO_RUN));

  const selectedVehicle = selectedVehicleId ? vehicles[selectedVehicleId] : null;
  const vehicleList = useMemo(() => Object.values(vehicles), [vehicles]);
  const activeAlerts = useMemo(() => alerts.filter((a) => !a.acknowledged), [alerts]);
  const routeList = useMemo(() => Object.values(routes), [routes]);

  const { startDemo } = useDemoFlight({ route: mockRoute });

  const handleStartDemo = useCallback(() => {
    startDemo();
  }, [startDemo]);

  const handleAutoFit = useCallback(() => {
    setAutoFit(true);
  }, [setAutoFit]);

  useEffect(() => {
    setRoute(mockRoute);
  }, [setRoute]);

  useEffect(() => {
    if (preselectedVehicleId && vehicles[preselectedVehicleId] && selectedVehicleId !== preselectedVehicleId) {
      selectVehicle(preselectedVehicleId);
    }
  }, [preselectedVehicleId, vehicles, selectedVehicleId, selectVehicle]);

  const hasNoData = vehicleList.length === 0 && routeList.length === 0;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h4" gutterBottom>
        GIS 전시기
      </Typography>

      <Toolbar
        variant="dense"
        sx={{ gap: 1, flexWrap: 'wrap', bgcolor: 'background.paper', borderRadius: 1, mb: 1, minHeight: 48 }}
      >
        <Button variant="outlined" size="small" startIcon={<PanoramaFishEyeIcon />} onClick={handleAutoFit}>
          전체 보기
        </Button>
        <Button
          variant={followVehicle ? 'contained' : 'outlined'}
          size="small"
          startIcon={<MyLocationIcon />}
          onClick={toggleFollowVehicle}
          disabled={!selectedVehicle}
        >
          UAV 추적
        </Button>
        <Button
          variant={showTracks ? 'contained' : 'outlined'}
          size="small"
          startIcon={<VisibilityIcon />}
          onClick={toggleShowTracks}
        >
          Track
        </Button>
        <Button
          variant={showRoutes ? 'contained' : 'outlined'}
          size="small"
          startIcon={<RouteIcon />}
          onClick={toggleShowRoutes}
        >
          Route
        </Button>
        <Button
          variant={showAlertPanel ? 'contained' : 'outlined'}
          size="small"
          startIcon={<WarningIcon />}
          onClick={toggleShowAlertPanel}
        >
          Alerts
        </Button>
        <Button
          variant={demoRunning ? 'contained' : 'outlined'}
          size="small"
          startIcon={<FlightTakeoffIcon />}
          onClick={demoRunning ? stopDemo : handleStartDemo}
          disabled={!hasDemoPermission}
        >
          {demoRunning ? 'Demo 중지' : 'Demo Flight'}
        </Button>
        {selectedVehicle && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={() => clearTrack(selectedVehicle.vehicleId)}
          >
            Track 삭제
          </Button>
        )}
      </Toolbar>

      {preselectedVehicleId && !selectedVehicle && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Query Parameter로 선택된 Vehicle: {preselectedVehicleId} (현재 데이터 없음)
        </Alert>
      )}

      <Box sx={{ flexGrow: 1, display: 'flex', gap: 1, minHeight: 0 }}>
        <Paper sx={{ width: { xs: '100%', md: '25%' }, minWidth: 200, p: 1, overflow: 'auto' }}>
          <VehicleList vehicles={vehicleList} selected={selectedVehicleId} onSelect={selectVehicle} />
        </Paper>

        <Paper sx={{ flexGrow: 1, minWidth: 0, position: 'relative', overflow: 'hidden' }}>
          <GisErrorBoundary>
            <Suspense
              fallback={
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="h6" gutterBottom>
                    GIS 엔진 초기화 중
                  </Typography>
                  <LinearProgress sx={{ mt: 2, mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Cesium 지도 및 자산을 로드하는 중입니다...
                  </Typography>
                </Box>
              }
            >
              <CesiumMap />
            </Suspense>
          </GisErrorBoundary>
          {hasNoData && (
            <Box sx={{ position: 'absolute', top: 8, left: 8, right: 8, zIndex: 1 }}>
              <Alert severity="info">
                메시지 이벤트가 없습니다. Generator에서 #101 메시지를 전송하거나 Demo Flight를 시작하세요.
              </Alert>
            </Box>
          )}
        </Paper>

        <Paper sx={{ width: { xs: '100%', md: '35%' }, minWidth: 250, p: 1, overflow: 'auto' }}>
          <VehicleDetailPanel
            vehicle={selectedVehicle}
            trackCount={selectedVehicleId ? (tracks[selectedVehicleId]?.length ?? 0) : 0}
          />
        </Paper>
      </Box>

      {showAlertPanel && (
        <Paper sx={{ maxHeight: 200, mt: 1, p: 1, overflow: 'auto' }}>
          <AlertPanel alerts={activeAlerts} onAcknowledge={acknowledgeAlert} onClear={clearAlerts} />
        </Paper>
      )}

      <Disclaimer />
    </Box>
  );
};

const VehicleList = ({
  vehicles,
  selected,
  onSelect,
}: {
  vehicles: VehicleState[];
  selected: string | null;
  onSelect: (id: string | null) => void;
}) => {
  if (vehicles.length === 0) {
    return (
      <Box sx={{ p: 1 }}>
        <Typography variant="body2" color="text.secondary">
          등록된 UAV가 없습니다.
        </Typography>
      </Box>
    );
  }
  return (
    <List dense>
      {vehicles.map((v) => (
        <ListItem key={v.vehicleId} disablePadding>
          <ListItemButton selected={selected === v.vehicleId} onClick={() => onSelect(v.vehicleId)}>
            <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
              <Typography variant="body1">{v.callsign || v.vehicleId}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Chip label={v.status} size="small" color={statusColor(v.status)} />
                <Typography variant="caption" color="text.secondary">
                  {v.coordinates.latitude.toFixed(4)}, {v.coordinates.longitude.toFixed(4)}
                </Typography>
              </Box>
            </Box>
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  );
};

const VehicleDetailPanel = ({ vehicle, trackCount }: { vehicle: VehicleState | null; trackCount: number }) => {
  if (!vehicle) {
    return (
      <Box sx={{ p: 1 }}>
        <Typography variant="body2" color="text.secondary">
          UAV를 선택하면 상세 정보가 표시됩니다.
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1 }}>
      <Typography variant="h6">{vehicle.callsign || vehicle.vehicleId}</Typography>
      <Divider />
      <DetailRow label="Vehicle ID" value={vehicle.vehicleId} />
      <DetailRow label="Status" value={vehicle.status} />
      <DetailRow label="Latitude" value={vehicle.coordinates.latitude.toFixed(6)} />
      <DetailRow label="Longitude" value={vehicle.coordinates.longitude.toFixed(6)} />
      <DetailRow label="Altitude" value={`${vehicle.coordinates.altitudeM.toFixed(1)} m`} />
      <DetailRow label="Heading" value={`${vehicle.headingDeg?.toFixed(1) ?? '-'}°`} />
      <DetailRow label="Speed" value={`${vehicle.speedMps?.toFixed(1) ?? '-'} m/s`} />
      <DetailRow label="Roll" value={`${vehicle.rollDeg?.toFixed(1) ?? '-'}°`} />
      <DetailRow label="Pitch" value={`${vehicle.pitchDeg?.toFixed(1) ?? '-'}°`} />
      <DetailRow label="Track Points" value={String(trackCount)} />
      <DetailRow label="Last Update" value={new Date(vehicle.updatedAt).toLocaleTimeString()} />
    </Box>
  );
};

const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
      {value}
    </Typography>
  </Box>
);

const AlertPanel = ({
  alerts,
  onAcknowledge,
  onClear,
}: {
  alerts: GisAlert[];
  onAcknowledge: (id: string) => void;
  onClear: () => void;
}) => {
  if (alerts.length === 0) {
    return (
      <Box sx={{ p: 1 }}>
        <Typography variant="body2" color="text.secondary">
          활성 알림이 없습니다.
        </Typography>
      </Box>
    );
  }
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2">활성 알림 ({alerts.length})</Typography>
        <Button size="small" startIcon={<CloseIcon />} onClick={onClear}>
          모두 인식
        </Button>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {alerts.map((alert) => (
          <Alert
            key={alert.id}
            severity={alert.severity === 'CRITICAL' ? 'error' : alert.severity === 'WARNING' ? 'warning' : 'info'}
            action={
              <IconButton size="small" color="inherit" onClick={() => onAcknowledge(alert.id)}>
                <CheckIcon fontSize="small" />
              </IconButton>
            }
          >
            <Typography variant="body2">
              [{alert.vehicleId}] {alert.message}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {new Date(alert.timestamp).toLocaleTimeString()}
            </Typography>
          </Alert>
        ))}
      </Box>
    </Box>
  );
};
